import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signIn,
  signOut,
} from "aws-amplify/auth";
import type { User } from "./authService";

export interface CognitoSignInResult {
  accessToken: string;
  idToken: string;
  user: User;
}

export type CognitoSignInResponse =
  | { kind: "complete"; session: CognitoSignInResult }
  | {
      kind: "new_password_required";
      missingAttributes?: string[];
    };

export interface CognitoCompleteNewPasswordInput {
  newPassword: string;
  userAttributes?: Record<string, string>;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length < 2) {
    throw new Error("Invalid token");
  }
  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  const json = atob(padded);
  return JSON.parse(json) as Record<string, unknown>;
}

function mapIdTokenToUser(idToken: string): User {
  const claims = decodeJwtPayload(idToken);
  const email =
    typeof claims.email === "string"
      ? claims.email
      : typeof claims["cognito:username"] === "string"
        ? claims["cognito:username"]
        : undefined;
  const name =
    typeof claims.name === "string"
      ? claims.name
      : typeof claims.given_name === "string"
        ? claims.given_name
        : email;

  const groups = claims["cognito:groups"];
  const roles = Array.isArray(groups)
    ? groups.filter((g): g is string => typeof g === "string")
    : typeof claims.roles === "string"
      ? [claims.roles]
      : Array.isArray(claims.roles)
        ? claims.roles.filter((r): r is string => typeof r === "string")
        : [];

  return {
    _id: typeof claims.sub === "string" ? claims.sub : undefined,
    email,
    name,
    roles,
    role: roles[0],
  };
}

function cognitoErrorMessage(error: unknown, fallback = "Request failed. Please try again."): string {
  if (error && typeof error === "object" && "name" in error) {
    const name = String((error as { name: string }).name);
    const message =
      "message" in error ? String((error as { message: unknown }).message) : "";

    switch (name) {
      case "NotAuthorizedException":
        if (message.includes("SECRET_HASH")) {
          return "Cognito app client requires a client secret. Use a public SPA client (no client secret) and update VITE_COGNITO_CLIENT_ID.";
        }
        return "Incorrect email or password.";
      case "UserNotConfirmedException":
        return "Account is not confirmed. Please verify your email.";
      case "UserNotFoundException":
        return "No account found with this email.";
      case "PasswordResetRequiredException":
        return "Password reset is required. Please reset your password.";
      case "CodeMismatchException":
        return "Invalid verification code. Please try again.";
      case "ExpiredCodeException":
        return "Verification code has expired. Request a new code.";
      case "InvalidPasswordException":
        return message || "Password does not meet requirements.";
      case "LimitExceededException":
      case "TooManyRequestsException":
        return "Too many attempts. Please try again later.";
      default:
        return message || fallback;
    }
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

async function fetchCognitoSession(): Promise<CognitoSignInResult> {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  const idToken = session.tokens?.idToken?.toString();

  if (!accessToken || !idToken) {
    throw new Error("Sign in succeeded but tokens were not returned.");
  }

  return {
    accessToken,
    idToken,
    user: mapIdTokenToUser(idToken),
  };
}

export const cognitoAuthService = {
  async signIn(email: string, password: string): Promise<CognitoSignInResponse> {
    try {
      // Clear any stale Cognito session before a fresh password sign-in.
      try {
        await getCurrentUser();
        await signOut();
      } catch {
        // No existing session — continue.
      }

      const result = await signIn({
        username: email,
        password,
      });

      if (
        result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        return {
          kind: "new_password_required",
          missingAttributes: result.nextStep.missingAttributes,
        };
      }

      if (result.nextStep.signInStep !== "DONE") {
        throw new Error(
          `Additional sign-in step required: ${result.nextStep.signInStep}`,
        );
      }

      return {
        kind: "complete",
        session: await fetchCognitoSession(),
      };
    } catch (error) {
      throw new Error(cognitoErrorMessage(error, "Sign in failed. Please try again."));
    }
  },

  async completeNewPasswordChallenge(
    input: CognitoCompleteNewPasswordInput,
  ): Promise<CognitoSignInResult> {
    try {
      const result = await confirmSignIn({
        challengeResponse: input.newPassword,
        ...(input.userAttributes && Object.keys(input.userAttributes).length > 0
          ? { options: { userAttributes: input.userAttributes } }
          : {}),
      });

      if (result.nextStep.signInStep !== "DONE") {
        throw new Error(
          `Additional sign-in step required: ${result.nextStep.signInStep}`,
        );
      }

      return fetchCognitoSession();
    } catch (error) {
      throw new Error(
        cognitoErrorMessage(error, "Unable to set new password. Please try again."),
      );
    }
  },

  async requestPasswordReset(email: string): Promise<void> {
    try {
      const result = await resetPassword({ username: email.trim() });
      if (
        result.nextStep.resetPasswordStep !==
        "CONFIRM_RESET_PASSWORD_WITH_CODE"
      ) {
        throw new Error(
          `Password reset step not supported: ${result.nextStep.resetPasswordStep}`
        );
      }
    } catch (error) {
      throw new Error(
        cognitoErrorMessage(error, "Unable to send reset code. Please try again.")
      );
    }
  },

  async confirmPasswordReset(
    email: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<void> {
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: confirmationCode.trim(),
        newPassword,
      });
    } catch (error) {
      throw new Error(
        cognitoErrorMessage(error, "Unable to reset password. Please try again.")
      );
    }
  },

  async signOut(): Promise<void> {
    try {
      await signOut();
    } catch {
      // Best-effort — local storage is cleared by the caller either way.
    }
  },

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  },

  /** Id token for API Authorization — preferred by most Cognito resource servers. */
  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  },

  async restoreSession(): Promise<CognitoSignInResult | null> {
    try {
      await getCurrentUser();
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      const idToken = session.tokens?.idToken?.toString();
      if (!accessToken || !idToken) return null;
      return {
        accessToken,
        idToken,
        user: mapIdTokenToUser(idToken),
      };
    } catch {
      return null;
    }
  },
};
